import ReactQuill, { Quill } from 'react-quill-new';
import type QuillEmbed from 'quill/blots/embed';

import {
  SubtextPlaceholder,
  getValueKey,
  parseSubtext,
} from './subtextPlaceholders';

// While a Text block (or element) is edited, each {placeholder} of one of its
// metrics shows as a chip of its metric's {name}, one character to
// the editor, so the caret steps over it and Backspace takes it whole. They
// exist only in the editor: its HTML has them as {placeholders} again
// (fromEditorHtml) before it's saved, so the saved text, and how dashboards
// read it, is as before.

export const METRIC_CHIP_CLASS = 'studio-metric-chip';
const METRIC_CHIP_BLOT = 'studioMetricChip';
// Stands for a chip in the editor's text, keeping its indices
const CHIP_CHAR = '\uFFFC';

const Embed = Quill.import('blots/embed') as typeof QuillEmbed;

// Its name is drawn from its attribute (see labelMetricChips), set as it's
// known
class MetricChipBlot extends Embed {
  static blotName = METRIC_CHIP_BLOT;

  static tagName = 'span';

  static className = METRIC_CHIP_CLASS;

  static create(key: string) {
    // An element, as its tagName says
    const node = super.create(key) as HTMLElement;
    node.setAttribute('data-key', key);
    node.setAttribute('data-testid', `studio-metric-chip-${key}`);
    return node;
  }

  static value(node: HTMLElement) {
    return node.getAttribute('data-key');
  }
}

Quill.register(MetricChipBlot);

type Editor = ReturnType<ReactQuill['getEditor']>;

// The editor's text from index, a chip standing as one character, so its
// indices are the editor's
export const getEditorText = ({
  editor,
  index = 0,
  length,
}: {
  editor: Editor;
  index?: number;
  length?: number;
}): string =>
  (editor.getContents(index, length).ops ?? [])
    .map((op) => (typeof op.insert === 'string' ? op.insert : CHIP_CHAR))
    .join('');

// A space after the chip at index, unless one's there already, and the
// caret after it, as the user's edit. The caret can't be put straight
// after a chip at the end of a line and typed from: what's typed there is
// lost.
export const spaceAfterChip = ({
  editor,
  index,
}: {
  editor: Editor;
  index: number;
}) => {
  const next = getEditorText({ editor, index: index + 1, length: 1 });
  if (!/^[ \u00a0]$/.test(next)) {
    editor.insertText(index + 1, ' ', 'user');
  }
  editor.setSelection(index + 2, 0, 'silent');
};

// Puts a chip in at index, as the user's edit
export const insertMetricChip = ({
  editor,
  index,
  key,
}: {
  editor: Editor;
  index: number;
  key: string;
}) => {
  editor.insertEmbed(index, METRIC_CHIP_BLOT, key, 'user');
};

// The saved rich text, its metrics' placeholders as chips
export const toEditorHtml = ({
  html,
  placeholders,
}: {
  html: string;
  placeholders: SubtextPlaceholder[];
}): string => {
  if (!html.includes('{') || placeholders.length === 0) {
    return html;
  }
  // By what each shows: a metric's value, or its change ({key.delta})
  const keyByValueKey = Object.fromEntries(
    placeholders.map((p) => [getValueKey(p), p.key]),
  );
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  const textNodes: Node[] = [];
  while (walker.nextNode()) {
    textNodes.push(walker.currentNode);
  }
  textNodes.forEach((node) => {
    const text = node.textContent ?? '';
    const parts = text.includes('{')
      ? parseSubtext({ text, placeholders })
      : [];
    if (!parts.some((part) => part.kind === 'metric')) {
      return;
    }
    const nodes = parts.map((part) => {
      if (part.kind === 'text') {
        return doc.createTextNode(part.text);
      }
      const chip = doc.createElement('span');
      chip.className = METRIC_CHIP_CLASS;
      chip.setAttribute('data-key', keyByValueKey[getValueKey(part)]);
      return chip;
    });
    (node as ChildNode).replaceWith(...nodes);
  });
  return doc.body.innerHTML;
};

// The editor's rich text, its chips as placeholders again, to save
export const fromEditorHtml = (html: string): string => {
  if (!html.includes(METRIC_CHIP_CLASS)) {
    return html;
  }
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.body.querySelectorAll(`span.${METRIC_CHIP_CLASS}`).forEach((chip) => {
    chip.replaceWith(doc.createTextNode(`{${chip.getAttribute('data-key')}}`));
  });
  return doc.body.innerHTML;
};

// Placeholders typed out in full turned into chips, without it counting as
// an edit (the text it saves is the same). The caret stays where it was in
// the text: after a chip it was at the end of, with a space after it (see
// spaceAfterChip).
export const chipTypedPlaceholders = ({
  editor,
  placeholders,
}: {
  editor: Editor;
  placeholders: SubtextPlaceholder[];
}) => {
  const keys = new Set(placeholders.map((p) => p.key));
  const text = getEditorText({ editor });
  const matches = Array.from(text.matchAll(/\{([^{}]*)\}/g)).filter((m) =>
    keys.has(m[1].trim()),
  );
  if (matches.length === 0) {
    return;
  }
  const range = editor.getSelection();
  let caret = range ? range.index : undefined;
  // The one just typed out, closed at the caret
  const justTyped = matches.find(
    (m) => caret !== undefined && (m.index ?? 0) + m[0].length === caret,
  );
  // From the end, so the earlier ones' indices hold
  [...matches].reverse().forEach((match) => {
    const index = match.index ?? 0;
    const { length } = match[0];
    editor.deleteText(index, length, 'silent');
    editor.insertEmbed(index, METRIC_CHIP_BLOT, match[1].trim(), 'silent');
    if (caret !== undefined && caret > index) {
      caret = caret >= index + length ? caret - length + 1 : index + 1;
    }
  });
  if (caret === undefined) {
    return;
  }
  if (justTyped) {
    spaceAfterChip({ editor, index: caret - 1 });
  } else {
    editor.setSelection(caret, 0, 'silent');
  }
};

// The chip a press landed on selected, so the format bar formats it. False
// where it wasn't on one.
export const selectPressedChip = ({
  editor,
  target,
}: {
  editor: Editor;
  target: EventTarget | null;
}): boolean => {
  const chip =
    target instanceof Element
      ? target.closest(`span.${METRIC_CHIP_CLASS}`)
      : null;
  const blot = chip ? Quill.find(chip) : null;
  if (!blot || blot instanceof Quill) {
    return false;
  }
  editor.setSelection(editor.getIndex(blot), 1, 'user');
  return true;
};

// The chip that's selected on its own (as a press selects it), and its key
export const getSelectedChip = ({
  editor,
}: {
  editor: Editor;
}): { index: number; key: string } | undefined => {
  const range = editor.getSelection();
  if (!range || range.length !== 1) {
    return undefined;
  }
  const [op] = editor.getContents(range.index, 1).ops ?? [];
  const insert = op ? op.insert : undefined;
  if (!insert || typeof insert !== 'object') {
    return undefined;
  }
  const key = (insert as Record<string, unknown>)[METRIC_CHIP_BLOT];
  return typeof key === 'string' ? { index: range.index, key } : undefined;
};

const Delta = Quill.import('delta');

// The chip at index swapped for one of another key, e.g. a metric's change
// ({key.delta}) for its value, as the user's edit. It keeps its formatting
// (bold, colour, size, link) and stays selected.
export const setChipKey = ({
  editor,
  index,
  key,
}: {
  editor: Editor;
  index: number;
  key: string;
}) => {
  const formats = editor.getFormat(index, 1);
  editor.updateContents(
    new Delta()
      .retain(index)
      .delete(1)
      .insert({ [METRIC_CHIP_BLOT]: key }, formats),
    'user',
  );
  // Told about (not silently), so its panel still shows it's selected
  editor.setSelection(index, 1, 'api');
};

// The chips inside the selection marked, so they show as selected: the
// browser doesn't highlight them, as they can't be edited
export const markSelectedChips = ({ editor }: { editor: Editor }) => {
  const range = editor.getSelection();
  editor.root.querySelectorAll(`span.${METRIC_CHIP_CLASS}`).forEach((chip) => {
    const blot = Quill.find(chip);
    const index = blot && !(blot instanceof Quill) ? editor.getIndex(blot) : -1;
    const isSelected =
      !!range &&
      range.length > 0 &&
      index >= range.index &&
      index < range.index + range.length;
    if (isSelected !== chip.hasAttribute('data-selected')) {
      chip.toggleAttribute('data-selected', isSelected);
    }
  });
};

// Each chip's metric name, drawn from its attribute. Only set where it
// changes, as the editor watches its nodes.
export const labelMetricChips = ({
  root,
  names,
}: {
  root: HTMLElement;
  names: Record<string, string>;
}) => {
  root.querySelectorAll(`span.${METRIC_CHIP_CLASS}`).forEach((chip) => {
    const key = chip.getAttribute('data-key') ?? '';
    const name = names[key] ?? key;
    if (chip.getAttribute('data-name') !== name) {
      chip.setAttribute('data-name', name);
    }
  });
};
